/**
 * Visibility Diagnosis Tab Component
 *
 * Main component that combines all visibility diagnosis sub-components.
 * Used as a tab within the CrawlIntelligenceDashboard.
 */

import { useState, useEffect } from 'react';
import { useSite } from '@/contexts/SiteContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Eye, RefreshCw, Clock, AlertTriangle, Cpu, Search, FileCode, Lightbulb, Code, CheckCircle, XCircle } from 'lucide-react';

import { HealthScoreCards } from './HealthScoreCards';
import { CriticalIssuesPanel } from './CriticalIssuesPanel';
import { BotComparisonTable } from './BotComparisonTable';
import { RecommendationsList } from './RecommendationsList';

import {
  VisibilityDiagnosis,
  getVisibilityDiagnosis,
  runVisibilityDiagnosis,
} from '@/services/visibilityDiagnosisService';

export function VisibilityDiagnosisTab() {
  const { selectedSite } = useSite();
  const [diagnosis, setDiagnosis] = useState<VisibilityDiagnosis | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const siteId = selectedSite?.id;

  useEffect(() => {
    if (siteId) {
      loadDiagnosis();
    }
  }, [siteId]);

  const loadDiagnosis = async () => {
    if (!siteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getVisibilityDiagnosis(siteId);
      setDiagnosis(data);
    } catch (err) {
      setError('Failed to load visibility diagnosis');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleRunDiagnosis = async () => {
    if (!siteId) return;
    setRunning(true);
    setError(null);
    try {
      const data = await runVisibilityDiagnosis(siteId);
      setDiagnosis(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to run diagnosis');
      console.error(err);
    } finally {
      setRunning(false);
    }
  };

  if (!siteId) {
    return (
      <Alert>
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>Please select a site to view visibility diagnosis.</AlertDescription>
      </Alert>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
          <Skeleton className="h-40" />
        </div>
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Eye className="h-6 w-6" />
            AI & Search Engine Visibility Diagnosis
          </h2>
          {diagnosis?.analyzed_at && (
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
              <Clock className="h-3 w-3" />
              Last analyzed: {new Date(diagnosis.analyzed_at).toLocaleString()}
              <span className="ml-2">({Math.round(diagnosis.analysis_duration_ms / 1000)}s)</span>
            </p>
          )}
        </div>
        <Button onClick={handleRunDiagnosis} disabled={running}>
          <RefreshCw className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Running Diagnosis...' : diagnosis ? 'Re-run Diagnosis' : 'Run Diagnosis'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {!diagnosis ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Eye className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No Diagnosis Available</h3>
            <p className="text-muted-foreground text-center max-w-md mb-4">
              Run a visibility diagnosis to understand how AI systems (ChatGPT, Claude, Perplexity)
              and search engines perceive your site.
            </p>
            <Button onClick={handleRunDiagnosis} disabled={running}>
              <RefreshCw className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} />
              {running ? 'Running...' : 'Run Diagnosis'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList>
            <TabsTrigger value="overview" className="flex items-center gap-2">
              <Eye className="h-4 w-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="comparison" className="flex items-center gap-2">
              <Cpu className="h-4 w-4" />
              Bot Comparison
            </TabsTrigger>
            <TabsTrigger value="robots" className="flex items-center gap-2">
              <FileCode className="h-4 w-4" />
              Robots.txt
            </TabsTrigger>
            <TabsTrigger value="structured-data" className="flex items-center gap-2">
              <Code className="h-4 w-4" />
              Structured Data
            </TabsTrigger>
            <TabsTrigger value="recommendations" className="flex items-center gap-2">
              <Lightbulb className="h-4 w-4" />
              Recommendations
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {Object.keys(diagnosis.health_scores).length}
                  </div>
                  <p className="text-sm text-muted-foreground">Bots Analyzed</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold text-red-600">
                    {diagnosis.critical_issues.filter(i => i.severity === 'critical').length}
                  </div>
                  <p className="text-sm text-muted-foreground">Critical Issues</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {Object.values(diagnosis.health_scores).filter(s => s.is_ai_bot).length}
                  </div>
                  <p className="text-sm text-muted-foreground">AI Bots Tracked</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-6">
                  <div className="text-2xl font-bold">
                    {diagnosis.recommendations.length}
                  </div>
                  <p className="text-sm text-muted-foreground">Recommendations</p>
                </CardContent>
              </Card>
            </div>

            {/* Critical Issues */}
            <CriticalIssuesPanel issues={diagnosis.critical_issues} />

            {/* Health Score Cards */}
            <HealthScoreCards healthScores={diagnosis.health_scores} />
          </TabsContent>

          <TabsContent value="comparison" className="space-y-6">
            <BotComparisonTable
              sessionBehavior={diagnosis.session_behavior}
              healthScores={diagnosis.health_scores}
            />
          </TabsContent>

          <TabsContent value="robots" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileCode className="h-5 w-5" />
                  robots.txt Analysis
                </CardTitle>
                <CardDescription>
                  {diagnosis.robots_analysis.url}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {diagnosis.robots_analysis.status === 'found' ? (
                  <>
                    {/* AI Bot Rules */}
                    {diagnosis.robots_analysis.ai_bot_rules.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                          AI Bot Blocking Rules
                          <Badge variant="destructive">{diagnosis.robots_analysis.ai_bot_rules.length}</Badge>
                        </h4>
                        <div className="space-y-2">
                          {diagnosis.robots_analysis.ai_bot_rules.map((rule, i) => (
                            <div key={i} className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant={rule.impact === 'direct' ? 'destructive' : 'secondary'}>
                                  {rule.impact}
                                </Badge>
                                <code className="text-sm">{rule.user_agent}</code>
                              </div>
                              <code className="text-sm text-muted-foreground block">
                                {rule.type}: {rule.pattern}
                              </code>
                              <p className="text-sm mt-1">{rule.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Parameter Blocks */}
                    {diagnosis.robots_analysis.parameter_blocks.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Parameter-based Blocks</h4>
                        <div className="space-y-2">
                          {diagnosis.robots_analysis.parameter_blocks.map((block, i) => (
                            <div key={i} className="p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                              <code className="text-sm block mb-1">{block.rule}</code>
                              <p className="text-sm text-muted-foreground">{block.description}</p>
                              <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">{block.impact}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sitemaps */}
                    {diagnosis.robots_analysis.sitemaps.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Declared Sitemaps</h4>
                        <ul className="space-y-1">
                          {diagnosis.robots_analysis.sitemaps.map((sitemap, i) => (
                            <li key={i}>
                              <code className="text-sm text-blue-600">{sitemap}</code>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* All Rules Summary */}
                    <div>
                      <h4 className="font-medium mb-3">All Rules ({diagnosis.robots_analysis.rules.length})</h4>
                      <div className="max-h-64 overflow-y-auto border rounded-md">
                        <table className="w-full text-sm">
                          <thead className="bg-muted sticky top-0">
                            <tr>
                              <th className="p-2 text-left">User-Agent</th>
                              <th className="p-2 text-left">Type</th>
                              <th className="p-2 text-left">Pattern</th>
                            </tr>
                          </thead>
                          <tbody>
                            {diagnosis.robots_analysis.rules.slice(0, 50).map((rule, i) => (
                              <tr key={i} className={rule.affects_ai_bots ? 'bg-yellow-50 dark:bg-yellow-900/20' : ''}>
                                <td className="p-2 border-t">{rule.user_agent}</td>
                                <td className="p-2 border-t">
                                  <Badge variant={rule.type === 'disallow' ? 'destructive' : 'default'} className="text-xs">
                                    {rule.type}
                                  </Badge>
                                </td>
                                <td className="p-2 border-t font-mono text-xs">{rule.pattern}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </>
                ) : (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {diagnosis.robots_analysis.status === 'not_found'
                        ? 'No robots.txt file found at this URL.'
                        : `Error fetching robots.txt: ${diagnosis.robots_analysis.error}`}
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="structured-data" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Homepage Structured Data (JSON-LD)
                </CardTitle>
                <CardDescription>
                  {diagnosis.structured_data?.url || 'Schema.org markup detected on homepage'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {diagnosis.structured_data?.status === 'success' ? (
                  <>
                    {/* Schema Summary */}
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div className={`p-4 rounded-lg border ${diagnosis.structured_data.has_organization ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {diagnosis.structured_data.has_organization ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-red-600" />
                          )}
                          <span className="font-medium">Organization</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {diagnosis.structured_data.has_organization ? 'Found' : 'Missing'}
                        </p>
                      </div>
                      <div className={`p-4 rounded-lg border ${diagnosis.structured_data.has_website ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {diagnosis.structured_data.has_website ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-yellow-600" />
                          )}
                          <span className="font-medium">WebSite</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {diagnosis.structured_data.has_website ? 'Found' : 'Missing'}
                        </p>
                      </div>
                      <div className={`p-4 rounded-lg border ${diagnosis.structured_data.has_search_action ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'}`}>
                        <div className="flex items-center gap-2 mb-1">
                          {diagnosis.structured_data.has_search_action ? (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          ) : (
                            <XCircle className="h-4 w-4 text-yellow-600" />
                          )}
                          <span className="font-medium">SearchAction</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {diagnosis.structured_data.has_search_action ? 'Found' : 'Missing'}
                        </p>
                      </div>
                    </div>

                    {/* Detected Schemas */}
                    {diagnosis.structured_data.raw_types?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Detected Schema Types</h4>
                        <div className="flex flex-wrap gap-2">
                          {diagnosis.structured_data.raw_types.map((type, i) => (
                            <Badge key={i} variant="outline" className="text-sm">
                              {type}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Schema Details */}
                    {diagnosis.structured_data.schemas?.length > 0 && (
                      <div>
                        <h4 className="font-medium mb-3">Schema Details ({diagnosis.structured_data.schemas.length})</h4>
                        <div className="space-y-2">
                          {diagnosis.structured_data.schemas.slice(0, 10).map((schema, i) => (
                            <div key={i} className="p-3 bg-muted/50 rounded-md">
                              <div className="flex items-center gap-2">
                                <Badge>{schema.type}</Badge>
                                {schema.name && <span className="text-sm font-medium">{schema.name}</span>}
                              </div>
                              <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                                {schema.hasRating && <span className="text-green-600">Has Rating</span>}
                                {schema.hasContactPoint && <span className="text-green-600">Has Contact</span>}
                                {schema.hasPotentialAction && <span className="text-green-600">Has Actions</span>}
                              </div>
                            </div>
                          ))}
                          {diagnosis.structured_data.schemas.length > 10 && (
                            <p className="text-sm text-muted-foreground">
                              + {diagnosis.structured_data.schemas.length - 10} more schemas
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : diagnosis.structured_data?.status === 'error' ? (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      Could not fetch homepage structured data: {diagnosis.structured_data.error}
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      No structured data analysis available. Run a new diagnosis to analyze homepage schemas.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="recommendations" className="space-y-6">
            <RecommendationsList recommendations={diagnosis.recommendations} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
